import { Injectable } from '@angular/core';
import { RuntimeConfigurationService } from 'gain-web/shared-modules/runtime-configuration/runtime-configuration.service';

@Injectable({ providedIn: 'root' })
export class CalculationContextCacheService {
  private dbPromise?: Promise<IDBDatabase>;

  constructor(private runtimeConfigService: RuntimeConfigurationService) {}

  private async getDb(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      if (!this.runtimeConfigService.isConfigInitialized()) {
        await this.runtimeConfigService.init();
      }
      const dbName =
        this.runtimeConfigService.get().indexedDbName || 'GainAppDB';
      this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(dbName, 1);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains('calculationContext')) {
            db.createObjectStore('calculationContext');
          }
        };
        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = () => {
          reject(request.error);
        };
      });
    }
    return this.dbPromise;
  }

  async get<T>(key: string): Promise<T | null> {
    const db = await this.getDb();
    return new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction('calculationContext', 'readonly');
      const store = tx.objectStore('calculationContext');
      const request = store.get(key);
      request.onsuccess = () => {
        resolve(request.result ?? null);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async set<T>(key: string, value: T): Promise<void> {
    const db = await this.getDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction('calculationContext', 'readwrite');
      const store = tx.objectStore('calculationContext');
      const request = store.put(value, key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async remove(key: string): Promise<void> {
    const db = await this.getDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction('calculationContext', 'readwrite');
      const store = tx.objectStore('calculationContext');
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async keys(): Promise<string[]> {
    const db = await this.getDb();
    return new Promise<string[]>((resolve, reject) => {
      const tx = db.transaction('calculationContext', 'readonly');
      const store = tx.objectStore('calculationContext');
      const request = store.getAllKeys();
      request.onsuccess = () => {
        resolve(request.result as string[]);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  }
}
